import { AfterViewInit, Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgxCaptureService } from 'ngx-capture';
import { SpinnerService } from '@osc-utils/services/spinner.service';
import { Subscription } from 'rxjs';
import { ElementType, Location } from 'src/app/_objects/element-type';
import { ReportPage } from 'src/app/_objects/report-page';
import { FromToDetail, ReportTemplate } from 'src/app/_objects/report-template';
import { MousePoint, Notifier, PageScreen, ReportPreviewMetadata, ReportTemplateDesignService } from 'src/app/_services/report-template-design.service';

class Ghost {
  public readonly canvasWidth: number;
  public readonly canvasHeight: number;
  public preview: ReportPreviewMetadata;

  constructor(canvasWidth: number, canvasHeight: number) {
    this.clear();
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
  }

  public clear() {
    this.preview = {
      leftPx: 0,
      topPx: 0,
      widthPx: 0,
      heightPx: 0
    };
  }

  public getDrawMetadata(): ReportPreviewMetadata {
    return this.preview;
  }
}

@Component({
    selector: 'app-report-page-container',
    templateUrl: './report-page.component.html',
    styleUrls: ['./report-page.component.scss'],
    standalone: false
})
export class ReportPageComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() public pageKey: string;
  @Input() public isNewApp: any;
  @ViewChild('pageScreen', { static: true }) pageScreen: any;

  public reportPage: ReportPage;
  public fontFamily: string;

  public headerTextKeys: Array<string>;
  public footerTextKeys: Array<string>;
  public bodyTextKeys: Array<string>;

  public headerLineKeys: Array<string>;
  public footerLineKeys: Array<string>;
  public bodyLineKeys: Array<string>;

  public headerImageKeys: Array<string>;
  public footerImageKeys: Array<string>;
  public bodyImageKeys: Array<string>;

  public headerRectangleKeys: Array<string>;
  public footerRectangleKeys: Array<string>;
  public bodyRectangleKeys: Array<string>;

  public headerSummaryKeys: Array<string>;
  public footerSummaryKeys: Array<string>;
  public bodySummaryKeys: Array<string>;

  public headerSubTotalKeys: Array<string>;
  public footerSubTotalKeys: Array<string>;
  public bodySubTotalKeys: Array<string>;

  public headerPictureKeys: Array<string>;
  public footerPictureKeys: Array<string>;
  public bodyPictureKeys: Array<string>;

  public headerDataPlateKeys: Array<string>;
  public footerDataPlateKeys: Array<string>;
  public bodyDataPlateKeys: Array<string>;

  public headerTableKeys: Array<string>;
  public footerTableKeys: Array<string>;
  public bodyTableKeys: Array<string>;

  public headerMeasurementPointKey: Array<string>;
  public footerMeasurementPointKey: Array<string>;
  public bodyMeasurementPointKey: Array<string>;

  public headerVisualEventsKeys: Array<string>;
  public footerVisualEventsKeys: Array<string>;
  public bodyVisualEventsKeys: Array<string>;

  public headerSignatureKeys: Array<string>;
  public footerSignatureKeys: Array<string>;
  public bodySignatureKeys: Array<string>;

  public bodyInteractiveIsoKeys: Array<string>;

  public bodyDropDownListKeys: Array<string>;
  public bodyCustomElementsKeys: Array<string>;

  public sectionKeys: Array<string>;

  private isUsingNewDesigner = false;

  private notifier: Notifier;
  private mySubscriber: Subscription[];
  private ghostCanvas: HTMLCanvasElement;
  private ghostCanvasCtx: CanvasRenderingContext2D;
  private ghost: Ghost;
  private onDrawingBind;
  private resizing: boolean;
  public cmRuler: number[];
  public mmRuler: number[];
  public cmSideRuler: number[];
  public sideRulerLeftMargin: string;
  public sideRulerTopMargin: string;
  public readonly defaultRuler = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]; //creating default array for ngFor in ruler
  public showAddElement = false;
  public zoomPageMatValue = 1;
  constructor(private router: Router,
    public designService: ReportTemplateDesignService,
    public captureService: NgxCaptureService,
    private spinnerService: SpinnerService,
  ) {
    this.mmRuler = this.defaultRuler;
    this.cmRuler = this.defaultRuler;
    this.cmSideRuler = this.defaultRuler;
  }

  public ngOnInit() {

    // USED to avoid calling the snap method that causes the page to completely freeze
    // + gives memory-leaks - it seems that this was meant to be used with the new template designer
    // but the feature is not out
    this.isUsingNewDesigner = this.router.url.includes('report-template-new-design')

    this.notifier = {
      componentName: 'report-page.component',
      ids: { pageId: this.pageKey, elementId: '' }
    };

    this.reportPage = this.designService.getPage(this.pageKey);
    this.fontFamily = this.designService.getTemplateFontFamily();
    this.sideRulerLeftMargin = -(parseInt(this.reportPage.paddingLeft) + 4.75) + 'mm'; //4.75 is the default value
    this.sideRulerTopMargin = -(parseInt(this.reportPage.paddingTop) + parseInt(this.reportPage.headerHeight)) + 'mm';
    this.adjustRulerHeight(this.reportPage.paperSize.getPaperSize());
    this.designService.navigatorElements.push({
      name: this.reportPage.getTitle(),
      pageKey: this.pageKey,
      elementsDetails: [
        { location: 'Header', isVisible: false, elements: [] },
        { location: 'Body', isVisible: false, elements: [] },
        { location: 'Footer', isVisible: false, elements: [] }
      ]
    });
    this.headerTextKeys = this.designService.getTextIds(this.pageKey, 'Header');
    this.headerLineKeys = this.designService.getLineIds(this.pageKey, 'Header');
    this.headerImageKeys = this.designService.getImageIds(this.pageKey, 'Header');
    this.headerRectangleKeys = this.designService.getRectangleIds(this.pageKey, 'Header');
    this.headerSummaryKeys = this.designService.getSummaryIds(this.pageKey, 'Header');
    this.headerSubTotalKeys = this.designService.getSubTotalIds(this.pageKey, 'Header');
    this.headerPictureKeys = this.designService.getPictureIds(this.pageKey, 'Header');
    this.headerDataPlateKeys = this.designService.getDataPlateIds(this.pageKey, 'Header');
    this.headerMeasurementPointKey = this.designService.getMeasurementPointIds(this.pageKey, 'Header');
    this.headerVisualEventsKeys = this.designService.getVisualEventsIds(this.pageKey, 'Header');
    this.headerSignatureKeys = this.designService.getSignatureIds(this.pageKey, 'Header');
    this.headerTableKeys = this.designService.getTableIds(this.pageKey, 'Header');

    this.bodyTextKeys = this.designService.getTextIds(this.pageKey, 'Body');
    this.bodyLineKeys = this.designService.getLineIds(this.pageKey, 'Body');
    this.bodyImageKeys = this.designService.getImageIds(this.pageKey, 'Body');
    this.bodyRectangleKeys = this.designService.getRectangleIds(this.pageKey, 'Body');
    this.bodySummaryKeys = this.designService.getSummaryIds(this.pageKey, 'Body');
    this.bodySubTotalKeys = this.designService.getSubTotalIds(this.pageKey, 'Body');
    this.bodyPictureKeys = this.designService.getPictureIds(this.pageKey, 'Body');
    this.bodyDataPlateKeys = this.designService.getDataPlateIds(this.pageKey, 'Body');
    this.bodyMeasurementPointKey = this.designService.getMeasurementPointIds(this.pageKey, 'Body');
    this.bodyVisualEventsKeys = this.designService.getVisualEventsIds(this.pageKey, 'Body');
    this.bodySignatureKeys = this.designService.getSignatureIds(this.pageKey, 'Body');
    this.bodyTableKeys = this.designService.getTableIds(this.pageKey, 'Body');
    this.bodyInteractiveIsoKeys = this.designService.getInteractiveIsoIds(this.pageKey, 'Body');
    this.bodyDropDownListKeys = this.designService.getDropDownListIds(this.pageKey, 'Body');
    this.bodyCustomElementsKeys = this.designService.getCustomElementIds(this.pageKey, 'Body');

    this.footerTextKeys = this.designService.getTextIds(this.pageKey, 'Footer');
    this.footerLineKeys = this.designService.getLineIds(this.pageKey, 'Footer');
    this.footerImageKeys = this.designService.getImageIds(this.pageKey, 'Footer');
    this.footerRectangleKeys = this.designService.getRectangleIds(this.pageKey, 'Footer');
    this.footerSummaryKeys = this.designService.getSummaryIds(this.pageKey, 'Footer');
    this.footerSubTotalKeys = this.designService.getSubTotalIds(this.pageKey, 'Footer');
    this.footerPictureKeys = this.designService.getPictureIds(this.pageKey, 'Footer');
    this.footerDataPlateKeys = this.designService.getDataPlateIds(this.pageKey, 'Footer');
    this.footerMeasurementPointKey = this.designService.getMeasurementPointIds(this.pageKey, 'Footer');
    this.footerVisualEventsKeys = this.designService.getVisualEventsIds(this.pageKey, 'Footer');
    this.footerSignatureKeys = this.designService.getSignatureIds(this.pageKey, 'Footer');
    this.footerTableKeys = this.designService.getTableIds(this.pageKey, 'Footer');

    this.sectionKeys = this.designService.getSectionIds(this.pageKey, 'Body');

    this.mySubscriber = [];
    this.mySubscriber.push(
      this.designService.onEndDraw.subscribe(() => {
        this.designService.alignElementsWithSectionOnPage(this.pageKey);
      })
    );

    this.onDrawingBind = this.onDrawing.bind(this);

    this.mySubscriber.push(
      this.designService.onUpdatePageSnap.subscribe((message: string) => {
        this.takeSnapOfPage(true);
      })
    );
    this.mySubscriber.push(
      this.designService.currentZoomPage.subscribe((res: number) => {
        if (res) {
          this.zoomPageMatValue = res;
        }
      })
    );
  }

  public ngOnDestroy() {
    this.mySubscriber.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  public ngAfterViewInit() {
    if (!this.reportPage.isExisting() && !this.designService.isRedirectingToCustomElement) {
      this.router.navigate([], { fragment: this.reportPage.getAnchorId() });
      document.querySelector('#' + this.reportPage.getAnchorId()).scrollIntoView();
    }

    this.takeSnapOfPage(false);
  }

  public adjustRulerHeight(paperSize) {
    switch (paperSize) {
      case 'Letter':
        this.cmSideRuler = [...this.defaultRuler, 11, 12, 13];
        break;
      case 'Legal':
        this.cmSideRuler = [...this.defaultRuler, 11, 12, 13, 14, 15, 16];
        break;
      case 'A4':
        this.cmSideRuler = [...this.defaultRuler, 11, 12, 13, 14];
        break;
      case 'B5':
        this.cmSideRuler = [...this.defaultRuler, 11, 12, 13, 14];
        break;
      case 'B5JIS':
        this.cmSideRuler = [...this.defaultRuler, 11, 12, 13, 14];
        break;
    }
  }

  public getCanvasWidth(): number {
    return this.reportPage.getWidthPx(Location.Body);
  }

  public getCanvasHeight(locStr: string): number {
    const location: Location = Location.cast(locStr);
    return this.reportPage.getHeightPx(location);
  }

  public onClick(event: MouseEvent) {
    if (this.reportPage.isSelectable && !this.designService.isRedirectingToCustomElement) {
      this.designService.clickedPageIdForDelete = this.pageKey;
      this.designService.notifySelectElement(this.notifier, this.pageKey, event.ctrlKey);
      this.designService.notifyChangeScroll(
        this.notifier,
        this.pageKey,
        true
      );
      event.stopPropagation();
    }
  }

  public onBeginDraw(event: MouseEvent) {
    let template: ReportTemplate = { ...this.designService.originTemplate };
    let templateContent = JSON.stringify(this.designService.getTemplate());
    template.reportTemplateContent = templateContent;
    this.designService.templateForUndo.push(template);
    const elementType = this.designService.getElementType();
    this.ghostCanvas = event.target as HTMLCanvasElement;
    this.ghostCanvasCtx = this.ghostCanvas.getContext('2d');
    this.ghostCanvasCtx.strokeStyle = '#4894f8';
    this.ghost = new Ghost(this.ghostCanvas.width, this.ghostCanvas.height);
    this.ghost.preview.leftPx = event.offsetX;
    this.ghost.preview.topPx = event.offsetY;
    //Providing a default height width in case of drag
    if (elementType == ElementType.Text || elementType == ElementType.Section || elementType == ElementType.DropDownList || elementType == ElementType.Rectangle) {
      this.ghost.preview.widthPx = 100;
      this.ghost.preview.heightPx = 30;
    }
    if (elementType == ElementType.Image) {
      this.ghost.preview.widthPx = 100;
      this.ghost.preview.heightPx = 50;
    }
    if (elementType == ElementType.Line) {
      this.ghost.preview.widthPx = 120;
    }
    this.ghostCanvas.removeEventListener('mousemove', this.onDrawingBind);
    this.ghostCanvas.addEventListener('mousemove', this.onDrawingBind);
  }

  public onDrawing(event: MouseEvent) {
    this.ghostCanvasCtx.clearRect(0, 0, this.ghost.canvasWidth, this.ghost.canvasHeight);
    this.ghost.preview.widthPx = event.offsetX - this.ghost.preview.leftPx;
    this.ghost.preview.heightPx = event.offsetY - this.ghost.preview.topPx;
    const fitGrid = this.designService.fitVirtualGrid(this.ghost.getDrawMetadata());
    if (this.designService.getElementType() === ElementType.Line) {
      if (fitGrid.widthPx > fitGrid.heightPx) {
        this.ghostCanvasCtx.strokeRect(fitGrid.leftPx, fitGrid.topPx, fitGrid.widthPx, 0);
      } else {
        this.ghostCanvasCtx.strokeRect(fitGrid.leftPx, fitGrid.topPx, 0, fitGrid.heightPx);
      }
    } else {
      this.ghostCanvasCtx.strokeRect(fitGrid.leftPx, fitGrid.topPx, fitGrid.widthPx, fitGrid.heightPx);
    }
  }

  public onEndDraw(event: MouseEvent, locStr: string) {
    this.designService.highlightElement = false;
    const location: Location = Location.cast(locStr);
    if (!(this.ghost && this.ghostCanvas && this.ghostCanvasCtx)) {
      // ghost, ghostCanvas, ghostCanvasCtxのうち、どれか一つでも
      // インスタンスが存在していない状態でmouseupされた場合は処理を抜ける
      // ＝もう一度mousedownからやり直し可能な状態にする
      return;
    }
    this.designService.notifyDraw(this.notifier, location, this.ghost.getDrawMetadata());

    // // このタイミングでrempveEventListenerを実施しても、正しくイベント処理を削除できない。（理由は不明。。。）
    // // Ghost描画用のCanvasは、ReportPageインスタンスが保持するプロパティに紐付いて（＝ngIfで参照して）
    // // canvasタグそのものの有無を制御している。canvasタグがDOM上に存在しなくなったタイミングで、
    // // canvasに紐付けたonBeginDraw／onDrawing／onEndDraw（イベント処理）や、
    // // canvasDOMインスタンス、canvasContextインスタンスも同時に削除されるので、
    // // canvasに関わるインスタンスの明示的な破棄処理は実施せず、ngIfによるcanvasタグ制御に任せる。
    // // （※canvasタグの有無に関わるReportPageのプロパティは、serviceクラス側で設定を行うため、ここでは変更しない）
    this.ghostCanvasCtx.clearRect(0, 0, this.ghost.canvasWidth, this.ghost.canvasHeight);
    this.ghostCanvas.removeEventListener('mousemove', this.onDrawingBind);
    // delete this.ghostCanvasCtx;
    delete this.ghostCanvas;
    this.takeSnapOfPage(true);
  }

  public onDragOver(event: DragEvent, locStr: string) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    this.designService.highlightElement = true;
  }

  public onDrop(event: DragEvent, locStr: string) {
    event.preventDefault();
    this.designService.highlightElement = false;
    let template = { ...this.designService.originTemplate };
    template.reportTemplateContent = JSON.stringify(this.designService.getTemplate());
    this.designService.templateForUndo.push(template);
    const dragElemId = event.dataTransfer.getData('text');

    const toDetail: FromToDetail = {
      location: Location.cast(locStr),
      pageId: this.pageKey
    };
    const dropPoint: MousePoint = {
      onElementX: event.offsetX,
      onElementY: event.offsetY
    };

    // console.log('ReportPage onDrop: %s', JSON.stringify({
    //   srcElement: event.srcElement.tagName + ' ' + event.srcElement.className,
    //   offsetX: event.offsetX,
    //   offsetY: event.offsetY,
    //   layerX: event.layerX,
    //   layerY: event.layerY
    // }, undefined, 4));

    this.designService.notifyDrop(this.notifier, dragElemId, toDetail, dropPoint);

    event.dataTransfer.clearData('text');
  }

  public takeSnapOfPage(isUpdate: boolean) {
    if (!this.isUsingNewDesigner) return;

    if (!isUpdate) {
      this.spinnerService.show(this.spinnerService.mainSpinnerId);
    }
    setTimeout(() => {
      this.captureService.getImage(this.pageScreen.nativeElement, true)
        .subscribe((res) => {
          if (isUpdate) {
            this.designService.pageScreens.find(x => x.pageId === this.pageKey).imageSrc = res;
          }
          else {
            this.designService.pageScreens.push({
              pageId: this.pageKey,
              imageSrc: res
            });
            const pageSnap: PageScreen[] = this.designService.getPageIds().map((i) => this.designService.pageScreens.filter(x => x).find((j) => j.pageId === i));
            this.designService.pageScreens = pageSnap.filter(x => x);
          }
        },
          (err) => {
            console.error(err);
            this.spinnerService.hide(this.spinnerService.mainSpinnerId);
          },
          () => {
            this.spinnerService.hide(this.spinnerService.mainSpinnerId);
          });
    }, 5)
  }

  public enableAddElement(pageHeight, lastElementHeight) {
    return parseInt(pageHeight) - parseInt(lastElementHeight) >= 30 ? true : false;
  }
}
